import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { format } from "date-fns";
import { AlertTriangle, FileSpreadsheet, Info, Printer, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { DateField } from "@/components/calculator/DateField";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeLoan,
  scheduleToCSV,
  validateLoan,
  type Frequency,
  type InterestType,
  type LoanError,
} from "@/lib/calculators/loan";
import {
  BD_BANKS,
  LOAN_TYPES,
  RATES_DISCLAIMER,
  type LoanType,
} from "@/data/bdBankRates";

/* ---------- Strings ---------- */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Loan Calculator", bn: "লোন ক্যালকুলেটর" },
  desc: {
    en: "Calculate loan payments for any Bangladeshi bank loan — personal, home, car, motorcycle, business or education. Compare reducing-balance vs flat rate, choose monthly, weekly or quarterly payments.",
    bn: "যেকোনো বাংলাদেশী ব্যাংক ঋণের কিস্তি হিসাব করুন — পার্সোনাল, হোম, কার, মোটরসাইকেল, বিজনেস বা শিক্ষা। রিডিউসিং ব্যালেন্স ও ফ্ল্যাট রেট তুলনা, মাসিক/সাপ্তাহিক/ত্রৈমাসিক কিস্তি বেছে নিন।",
  },

  loanType: { en: "Loan type", bn: "ঋণের ধরন" },
  bank: { en: "Bank preset", bn: "ব্যাংক প্রিসেট" },
  bankRateNote: {
    en: "Indicative rate — verify with bank before applying.",
    bn: "প্রদর্শিত হার ধারণা মাত্র — আবেদনের আগে ব্যাংকের সঙ্গে যাচাই করুন।",
  },
  loanAmount: { en: "Loan amount", bn: "ঋণের পরিমাণ" },
  rate: { en: "Annual interest rate", bn: "বার্ষিক সুদের হার" },
  tenure: { en: "Loan tenure", bn: "ঋণের মেয়াদ" },
  years: { en: "Years", bn: "বছর" },
  months: { en: "Months", bn: "মাস" },
  startDate: { en: "Start date", bn: "শুরুর তারিখ" },
  startDatePh: { en: "DD/MM/YYYY", bn: "দিন/মাস/বছর" },

  frequency: { en: "Payment frequency", bn: "কিস্তির ফ্রিকোয়েন্সি" },
  freqMonthly: { en: "Monthly", bn: "মাসিক" },
  freqBiweekly: { en: "Bi-weekly", bn: "দুই-সাপ্তাহিক" },
  freqWeekly: { en: "Weekly", bn: "সাপ্তাহিক" },
  freqQuarterly: { en: "Quarterly", bn: "ত্রৈমাসিক" },

  interestType: { en: "Interest type", bn: "সুদের ধরন" },
  reducing: { en: "Reducing balance", bn: "রিডিউসিং ব্যালেন্স" },
  flat: { en: "Flat rate", bn: "ফ্ল্যাট রেট" },
  reducingHint: {
    en: "Bank standard. Interest is charged on the outstanding balance.",
    bn: "ব্যাংকের স্ট্যান্ডার্ড। বকেয়া অংশের উপর সুদ ধরা হয়।",
  },
  flatHint: {
    en: "Common with NBFI / microfinance. Interest is charged on the original principal for the full term.",
    bn: "এনবিএফআই/মাইক্রোফাইন্যান্সে প্রচলিত। মূল ঋণের উপর পুরো মেয়াদের সুদ ধরা হয়।",
  },

  bnNumerals: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  reset: { en: "Reset", bn: "রিসেট" },

  payment: { en: "Periodic payment", bn: "কিস্তি (প্রতি বার)" },
  totalInterest: { en: "Total interest", bn: "মোট সুদ" },
  totalPayment: { en: "Total payment", bn: "মোট পরিশোধ" },
  principal: { en: "Principal", bn: "মূল" },
  interest: { en: "Interest", bn: "সুদ" },
  effectiveRate: { en: "Effective annual rate", bn: "প্রকৃত বার্ষিক হার" },
  advertisedRate: { en: "Advertised rate", bn: "প্রদর্শিত হার" },

  flatWarnTitle: { en: "Flat-rate warning", bn: "ফ্ল্যাট রেট সতর্কতা" },
  flatWarnBody: {
    en: "Flat-rate loans cost more than reducing-balance loans for the same advertised rate. The effective annual rate above shows the real cost.",
    bn: "একই প্রদর্শিত হারে ফ্ল্যাট রেট ঋণ রিডিউসিং ব্যালেন্সের চেয়ে বেশি ব্যয়বহুল। উপরের প্রকৃত বার্ষিক হার আসল খরচ দেখায়।",
  },

  schedule: { en: "Amortization schedule", bn: "অ্যামোর্টাইজেশন সিডিউল" },
  yearly: { en: "Yearly", bn: "বছরভিত্তিক" },
  perPeriod: { en: "Per period", bn: "প্রতি কিস্তি" },
  period: { en: "Period", bn: "কিস্তি #" },
  date: { en: "Date", bn: "তারিখ" },
  principalPaid: { en: "Principal paid", bn: "মূল পরিশোধ" },
  interestPaid: { en: "Interest paid", bn: "সুদ পরিশোধ" },
  balance: { en: "Balance", bn: "বকেয়া" },
  year: { en: "Year", bn: "বছর" },
  downloadCsv: { en: "Download CSV", bn: "CSV ডাউনলোড" },
  print: { en: "Print", bn: "প্রিন্ট" },

  // errors
  err_amount_too_small: { en: "Loan amount must be at least ৳1,000.", bn: "ঋণের পরিমাণ অন্তত ১,০০০ টাকা হতে হবে।" },
  err_tenure_zero: { en: "Tenure must be at least 1 month.", bn: "মেয়াদ অন্তত ১ মাস হতে হবে।" },
  err_rate_negative: { en: "Interest rate cannot be negative.", bn: "সুদের হার ঋণাত্মক হতে পারে না।" },

  // content
  whatTitle: { en: "What is a loan EMI?", bn: "ঋণের ইএমআই কী?" },
  whatBody: {
    en: "An EMI (Equated Monthly Installment) is the fixed payment a borrower makes every period to repay a loan. Each payment covers a portion of interest plus a portion of the outstanding principal. As the principal shrinks, the interest portion falls and the principal portion grows.",
    bn: "ইএমআই (Equated Monthly Installment) হলো ঋণ পরিশোধের জন্য প্রতি কিস্তিতে দেওয়া নির্দিষ্ট অর্থ। প্রতিটি কিস্তিতে কিছুটা সুদ ও কিছুটা মূল পরিশোধ হয়। বকেয়া কমার সঙ্গে সুদের অংশ কমে এবং মূলের অংশ বাড়ে।",
  },
  reduceVsFlatTitle: { en: "Reducing balance vs Flat rate", bn: "রিডিউসিং ব্যালেন্স বনাম ফ্ল্যাট রেট" },
  reduceVsFlatBody: {
    en: "Reducing balance charges interest only on what you still owe. Flat rate charges interest on the original principal for every period of the loan — even after you've paid most of it back. For the same advertised rate of 12%, a flat-rate loan can cost almost double in real interest. Always ask the lender for the effective annual rate before signing.",
    bn: "রিডিউসিং ব্যালেন্সে সুদ শুধু বকেয়া অংশের উপর ধরা হয়। ফ্ল্যাট রেটে পুরো মেয়াদের জন্য মূল ঋণের উপর সুদ গণনা হয় — আপনি বেশিরভাগ পরিশোধ করার পরও। ১২% একই প্রদর্শিত হারে ফ্ল্যাট রেট ঋণে প্রকৃত সুদ প্রায় দ্বিগুণ হতে পারে। চুক্তির আগে ঋণদাতাকে প্রকৃত বার্ষিক হার জিজ্ঞাসা করুন।",
  },
  tenureTitle: { en: "How to choose loan tenure", bn: "ঋণের মেয়াদ কীভাবে ঠিক করবেন" },
  tenureBody: {
    en: "Longer tenure means smaller payments but more total interest. Shorter tenure means bigger payments but less total interest. As a rule of thumb, your total monthly debt payments (across all loans) should not exceed 40% of take-home income.",
    bn: "মেয়াদ বেশি হলে কিস্তি ছোট হয় কিন্তু মোট সুদ বাড়ে। মেয়াদ কম হলে কিস্তি বড় হয় কিন্তু মোট সুদ কমে। সাধারণ নিয়ম: আপনার মোট মাসিক ঋণের কিস্তি (সব ঋণ মিলিয়ে) আয়ের ৪০%-এর বেশি হওয়া উচিত নয়।",
  },
  ratesTitle: { en: "Bangladeshi bank loan rate comparison", bn: "বাংলাদেশী ব্যাংকের ঋণের হার তুলনা" },
  prepayTitle: { en: "Pre-payment & foreclosure rules in BD", bn: "প্রি-পেমেন্ট ও ফোরক্লোজার নিয়ম" },
  prepayBody: {
    en: "Most Bangladeshi banks allow partial pre-payment after 6–12 months of regular EMI. Foreclosure (full early settlement) usually carries a 1–2% charge on the outstanding principal. Microfinance and NBFI loans may have stricter rules — read the agreement carefully.",
    bn: "বেশিরভাগ বাংলাদেশী ব্যাংক নিয়মিত ৬–১২ মাস কিস্তির পর আংশিক প্রি-পেমেন্ট অনুমতি দেয়। ফোরক্লোজার (সম্পূর্ণ আগাম পরিশোধ) সাধারণত বকেয়ার ১–২% চার্জ নেয়। মাইক্রোফাইন্যান্স ও এনবিএফআই ঋণে নিয়ম কঠোর হতে পারে — চুক্তি ভালোভাবে পড়ুন।",
  },
} as const;

/* ---------- FAQ ---------- */

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What is the difference between EMI and a loan calculator?", bn: "ইএমআই ও লোন ক্যালকুলেটরের পার্থক্য কী?" },
    a: {
      en: "An EMI calculator focuses on monthly equal installments only. A loan calculator is more general — it supports multiple payment frequencies (weekly, bi-weekly, monthly, quarterly) and both reducing-balance and flat-rate interest.",
      bn: "ইএমআই ক্যালকুলেটর শুধু মাসিক সমান কিস্তি হিসাব করে। লোন ক্যালকুলেটর বেশি সাধারণ — এতে সাপ্তাহিক, দুই-সাপ্তাহিক, মাসিক, ত্রৈমাসিক কিস্তি এবং রিডিউসিং ও ফ্ল্যাট রেট দুটোই সমর্থিত।",
    },
  },
  {
    q: { en: "Why is flat rate higher in real terms?", bn: "ফ্ল্যাট রেট প্রকৃত খরচে বেশি কেন?" },
    a: {
      en: "Flat-rate loans charge interest on the full original principal for every period — even after you've already paid down a lot of it. Reducing-balance loans only charge interest on what you still owe. For a 5-year, 12% loan, a flat rate's real (effective) cost is closer to 22% per year.",
      bn: "ফ্ল্যাট রেটে পুরো মেয়াদের জন্য মূল ঋণের উপর সুদ ধরা হয় — অনেকটা পরিশোধ করার পরও। রিডিউসিং ব্যালেন্সে শুধু বকেয়ার উপর সুদ। ৫ বছরের ১২% ঋণে ফ্ল্যাট রেটের প্রকৃত খরচ বছরে প্রায় ২২%।",
    },
  },
  {
    q: { en: "ব্যাংক লোন ক্যালকুলেটর কী?", bn: "ব্যাংক লোন ক্যালকুলেটর কী?" },
    a: {
      en: "এটি একটি ফ্রি টুল যা আপনাকে প্রতিটি বাংলাদেশী ব্যাংকের প্রস্তাবিত হার অনুযায়ী মাসিক/সাপ্তাহিক কিস্তি ও মোট সুদ হিসাব করে দেখায়।",
      bn: "এটি একটি ফ্রি টুল যা আপনাকে প্রতিটি বাংলাদেশী ব্যাংকের প্রস্তাবিত হার অনুযায়ী মাসিক/সাপ্তাহিক কিস্তি ও মোট সুদ হিসাব করে দেখায়।",
    },
  },
  {
    q: { en: "Which payment frequency is best?", bn: "কোন ফ্রিকোয়েন্সি সেরা?" },
    a: {
      en: "Bi-weekly payments save the most interest because you make 26 payments a year instead of 12, paying down the principal faster. Weekly is similar. Monthly is most common because most salaries are monthly.",
      bn: "দুই-সাপ্তাহিক (বছরে ২৬ কিস্তি) সবচেয়ে বেশি সুদ সাশ্রয় করে কারণ মূল দ্রুত কমে। সাপ্তাহিক প্রায় একই রকম। মাসিক সবচেয়ে প্রচলিত কারণ অধিকাংশ বেতন মাসিক।",
    },
  },
  {
    q: { en: "Can I pre-pay my loan?", bn: "আমি কি আগেই ঋণ পরিশোধ করতে পারি?" },
    a: {
      en: "Yes, most BD banks allow it after 6–12 months. Foreclosure typically incurs a 1–2% charge on the outstanding principal. Pre-payment significantly reduces the total interest you pay.",
      bn: "হ্যাঁ, বেশিরভাগ ব্যাংক ৬–১২ মাস পর অনুমতি দেয়। ফোরক্লোজারে সাধারণত বকেয়ার ১–২% চার্জ লাগে। প্রি-পেমেন্ট মোট সুদ অনেকটা কমায়।",
    },
  },
  {
    q: { en: "Are bank rates here exact?", bn: "এখানের ব্যাংক হার কি সঠিক?" },
    a: {
      en: "The bank presets are indicative based on published 2025 rate cards. Your actual rate depends on credit profile, loan size, tenure and current bank policy. Always confirm with the bank before applying.",
      bn: "ব্যাংক প্রিসেট ২০২৫ সালের প্রকাশিত হার অনুযায়ী আনুমানিক। আপনার প্রকৃত হার ক্রেডিট প্রোফাইল, ঋণের আকার, মেয়াদ ও বর্তমান ব্যাংক নীতির উপর নির্ভর করে। আবেদনের আগে ব্যাংকে যাচাই করুন।",
    },
  },
  {
    q: { en: "How much loan can I get on my salary?", bn: "আমার বেতনে কত ঋণ পাব?" },
    a: {
      en: "Most BD banks limit total monthly EMI to about 40–50% of your net monthly income. So on a take-home salary of ৳60,000, your maximum EMI capacity is around ৳24,000–৳30,000.",
      bn: "বেশিরভাগ বাংলাদেশী ব্যাংক মাসিক মোট ইএমআই-কে নেট মাসিক আয়ের ৪০–৫০%-এ সীমাবদ্ধ রাখে। যেমন ৬০,০০০ টাকা বেতনে সর্বোচ্চ ইএমআই সক্ষমতা প্রায় ২৪,০০০–৩০,০০০ টাকা।",
    },
  },
  {
    q: { en: "Does the calculator include processing fees?", bn: "ক্যালকুলেটরে কি প্রসেসিং ফি যুক্ত আছে?" },
    a: {
      en: "No — only principal and interest. Banks typically charge 1–2% as a one-time processing fee, and may add stamp duty, insurance and CIB charges. Add these separately when comparing offers.",
      bn: "না — শুধু মূল ও সুদ। ব্যাংক সাধারণত ১–২% এককালীন প্রসেসিং ফি নেয়, এছাড়া স্ট্যাম্প ডিউটি, ইনস্যুরেন্স ও সিআইবি চার্জ থাকতে পারে। অফার তুলনার সময় এগুলো আলাদা যোগ করুন।",
    },
  },
];

/* ---------- Route ---------- */

export const Route = createFileRoute("/loan-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Loan Calculator BD — Bank, Personal, Home Loan",
      description:
        "Free Bangladesh loan EMI calculator. Personal, home, car, motorcycle loans. Compare reducing balance vs flat rate. লোন ক্যালকুলেটর।",
      path: "/loan-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Loan Calculator", path: "/loan-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: LoanCalculatorPage,
});

/* ---------- Page ---------- */

const AMOUNT_MIN = 10_000;
const AMOUNT_MAX = 500_000_000; // 50 crore
const RATE_MIN = 0.1;
const RATE_MAX = 30;

function LoanCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  const [loanType, setLoanType] = useState<LoanType>("personal");
  const [bankId, setBankId] = useState<string>("custom");
  const [amount, setAmount] = useState<number>(500_000);
  const [amountStr, setAmountStr] = useState<string>("500000");
  const [rate, setRate] = useState<number>(12);
  const [rateStr, setRateStr] = useState<string>("12");
  const [tenureUnit, setTenureUnit] = useState<"years" | "months">("years");
  const [tenureValue, setTenureValue] = useState<number>(5);
  const [tenureStr, setTenureStr] = useState<string>("5");
  const [frequency, setFrequency] = useState<Frequency>("monthly");
  const [interestType, setInterestType] = useState<InterestType>("reducing");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [bnDigits, setBnDigits] = useState(false);

  // Sync number string -> numeric
  useEffect(() => {
    const v = parseFloat(amountStr);
    if (Number.isFinite(v)) setAmount(Math.min(AMOUNT_MAX, Math.max(0, v)));
  }, [amountStr]);
  useEffect(() => {
    const v = parseFloat(rateStr);
    if (Number.isFinite(v)) setRate(Math.min(RATE_MAX, Math.max(0, v)));
  }, [rateStr]);
  useEffect(() => {
    const v = parseFloat(tenureStr);
    if (Number.isFinite(v)) setTenureValue(Math.max(1, v));
  }, [tenureStr]);

  const applyLoanType = (id: LoanType) => {
    setLoanType(id);
    const lt = LOAN_TYPES.find((l) => l.id === id);
    if (!lt) return;
    setAmount(lt.defaultAmount);
    setAmountStr(String(lt.defaultAmount));
    setRate(lt.defaultRate);
    setRateStr(String(lt.defaultRate));
    setTenureUnit("years");
    setTenureValue(lt.defaultYears);
    setTenureStr(String(lt.defaultYears));
    setBankId("custom");
  };

  const applyBank = (id: string) => {
    setBankId(id);
    const bank = BD_BANKS.find((b) => b.id === id);
    if (!bank) return;
    const lt = loanType === "custom" ? "personal" : loanType;
    const r = bank.rates[lt as keyof typeof bank.rates] ?? bank.rates.personal;
    if (r != null) {
      setRate(r);
      setRateStr(String(r));
    }
  };

  const months = tenureUnit === "years" ? Math.round(tenureValue * 12) : Math.round(tenureValue);
  const validation = validateLoan({
    principal: amount,
    annualRatePct: rate,
    months,
    frequency,
    interestType,
  });
  const errKey: LoanError | null = validation.ok ? null : validation.error;

  const result = useMemo(() => {
    if (!validation.ok) return null;
    return computeLoan({
      principal: amount,
      annualRatePct: rate,
      months,
      frequency,
      interestType,
      startDate: startDate ?? undefined,
    });
  }, [amount, rate, months, frequency, interestType, startDate, validation.ok]);

  const reset = () => {
    applyLoanType("personal");
    setFrequency("monthly");
    setInterestType("reducing");
    setStartDate(new Date());
  };

  const downloadCSV = () => {
    if (!result) return;
    const csv = scheduleToCSV(result.schedule);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loan-schedule-${amount}-${rate}pc-${months}m-${frequency}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printRef = useRef<HTMLDivElement>(null);
  const doPrint = () => window.print();

  const chartData = result
    ? [
        { name: tr("principal"), value: amount, color: "var(--accent)" },
        {
          name: tr("interest"),
          value: Math.max(0, result.totalInterest),
          color: "var(--muted-foreground)",
        },
      ]
    : [];

  const errMessage = (() => {
    if (!errKey) return null;
    const map: Record<LoanError, keyof typeof STR | null> = {
      amount_too_small: "err_amount_too_small",
      tenure_zero: "err_tenure_zero",
      rate_negative: "err_rate_negative",
      missing: null,
    };
    const k = map[errKey];
    return k ? tr(k) : null;
  })();

  const freqLabel = (f: Frequency) =>
    ({ monthly: tr("freqMonthly"), biweekly: tr("freqBiweekly"), weekly: tr("freqWeekly"), quarterly: tr("freqQuarterly") }[f]);

  return (
    <CalculatorLayout
      slug="loan-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Banknote"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <TooltipProvider delayDuration={150}>
        {/* Loan type chips */}
        <div className="space-y-2">
          <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("loanType")}</p>
          <div className="flex flex-wrap gap-1.5">
            {LOAN_TYPES.map((lt) => {
              const active = loanType === lt.id;
              return (
                <button
                  key={lt.id}
                  type="button"
                  onClick={() => applyLoanType(lt.id)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                    active
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card text-foreground/80 hover:border-accent/40 hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {lt.label[lang]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bank + start date */}
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label
              className={cn(
                "flex items-center gap-1.5 text-sm font-medium",
                lang === "bn" && "font-bn",
              )}
            >
              {tr("bank")}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={13} className="text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className={cn(lang === "bn" && "font-bn")}>
                  {tr("bankRateNote")}
                </TooltipContent>
              </Tooltip>
            </label>
            <Select value={bankId} onValueChange={applyBank}>
              <SelectTrigger className={cn(lang === "bn" && "font-bn")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BD_BANKS.map((b) => (
                  <SelectItem key={b.id} value={b.id} className={cn(lang === "bn" && "font-bn")}>
                    {b.name[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DateField
            label={tr("startDate")}
            value={startDate}
            onChange={setStartDate}
            placeholder={tr("startDatePh")}
            fromYear={1990}
            toDate={new Date(2100, 11, 31)}
          />
        </div>

        {/* Loan amount */}
        <div className="mt-6 space-y-3">
          <NumberInput
            label={tr("loanAmount")}
            value={amountStr}
            onChange={setAmountStr}
            unit="৳"
            error={errKey === "amount_too_small" ? errMessage : null}
          />
          <Slider
            value={[amount]}
            min={AMOUNT_MIN}
            max={AMOUNT_MAX}
            step={1000}
            onValueChange={(v) => {
              setAmount(v[0]);
              setAmountStr(String(v[0]));
            }}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{formatBDT(AMOUNT_MIN, lang, 0)}</span>
            <span>{formatBDT(AMOUNT_MAX, lang, 0)}</span>
          </div>
        </div>

        {/* Rate */}
        <div className="mt-6 space-y-3">
          <NumberInput
            label={tr("rate")}
            value={rateStr}
            onChange={setRateStr}
            unit="%"
            error={errKey === "rate_negative" ? errMessage : null}
          />
          <Slider
            value={[rate]}
            min={RATE_MIN}
            max={RATE_MAX}
            step={0.1}
            onValueChange={(v) => {
              setRate(v[0]);
              setRateStr(String(v[0]));
            }}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{num(RATE_MIN)}%</span>
            <span>{num(RATE_MAX)}%</span>
          </div>
        </div>

        {/* Tenure */}
        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <NumberInput
              label={tr("tenure")}
              value={tenureStr}
              onChange={setTenureStr}
              unit={tenureUnit === "years" ? tr("years") : tr("months")}
              error={errKey === "tenure_zero" ? errMessage : null}
            />
            <Slider
              className="mt-3"
              value={[tenureValue]}
              min={1}
              max={tenureUnit === "years" ? 30 : 360}
              step={1}
              onValueChange={(v) => {
                setTenureValue(v[0]);
                setTenureStr(String(v[0]));
              }}
            />
          </div>
          <div className="self-end">
            <UnitToggle<"years" | "months">
              value={tenureUnit}
              onChange={(u) => {
                if (u === tenureUnit) return;
                if (u === "months") {
                  const m = Math.round(tenureValue * 12);
                  setTenureUnit("months");
                  setTenureValue(m);
                  setTenureStr(String(m));
                } else {
                  const y = Math.max(1, Math.round((tenureValue / 12) * 10) / 10);
                  setTenureUnit("years");
                  setTenureValue(y);
                  setTenureStr(String(y));
                }
              }}
              options={[
                { value: "years", label: tr("years") },
                { value: "months", label: tr("months") },
              ]}
            />
          </div>
        </div>

        {/* Frequency + interest type */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("frequency")}
            </label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as Frequency)}>
              <SelectTrigger className={cn(lang === "bn" && "font-bn")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly" className={cn(lang === "bn" && "font-bn")}>
                  {tr("freqMonthly")}
                </SelectItem>
                <SelectItem value="biweekly" className={cn(lang === "bn" && "font-bn")}>
                  {tr("freqBiweekly")}
                </SelectItem>
                <SelectItem value="weekly" className={cn(lang === "bn" && "font-bn")}>
                  {tr("freqWeekly")}
                </SelectItem>
                <SelectItem value="quarterly" className={cn(lang === "bn" && "font-bn")}>
                  {tr("freqQuarterly")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("interestType")}
            </label>
            <UnitToggle<InterestType>
              value={interestType}
              onChange={setInterestType}
              options={[
                { value: "reducing", label: tr("reducing") },
                { value: "flat", label: tr("flat") },
              ]}
            />
            <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {interestType === "reducing" ? tr("reducingHint") : tr("flatHint")}
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <label
            className={cn(
              "inline-flex items-center gap-2 text-xs text-muted-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            <input
              type="checkbox"
              checked={bnDigits}
              onChange={(e) => setBnDigits(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("bnNumerals")}
          </label>
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} />
            {tr("reset")}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <motion.div
            ref={printRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-8 space-y-6"
          >
            <div className="grid gap-3 md:grid-cols-3">
              <ResultCard
                label={`${tr("payment")} (${freqLabel(frequency)})`}
                value={moneyDisplay(result.payment, lang, bnDigits, 0)}
                accent
              />
              <ResultCard
                label={tr("totalInterest")}
                value={moneyDisplay(result.totalInterest, lang, bnDigits, 0)}
              />
              <ResultCard
                label={tr("totalPayment")}
                value={moneyDisplay(result.totalPayment, lang, bnDigits, 0)}
              />
            </div>

            {interestType === "flat" && (
              <div className="flex items-start gap-3 rounded-xl border border-yellow-500/40 bg-yellow-500/5 p-4">
                <AlertTriangle className="mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400" size={18} />
                <div className="space-y-1">
                  <p
                    className={cn(
                      "text-sm font-semibold text-yellow-700 dark:text-yellow-400",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {tr("flatWarnTitle")}
                  </p>
                  <p
                    className={cn(
                      "text-sm text-foreground/80",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {tr("effectiveRate")}: <strong>{num(result.effectiveAnnualRatePct.toFixed(2))}%</strong>{" "}
                    <span className="text-muted-foreground">
                      ({tr("advertisedRate")}: {num(rate)}%)
                    </span>
                  </p>
                  <p
                    className={cn(
                      "text-xs text-muted-foreground",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {tr("flatWarnBody")}
                  </p>
                </div>
              </div>
            )}

            {/* Donut chart */}
            <div className="grid items-center gap-6 rounded-xl border border-border bg-card p-4 md:grid-cols-[200px_1fr]">
              <div className="h-44">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(v: number) => moneyDisplay(v, lang, bnDigits, 0)}
                      contentStyle={{
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 text-sm">
                <Legend
                  color="var(--accent)"
                  label={tr("principal")}
                  value={moneyDisplay(amount, lang, bnDigits, 0)}
                  lang={lang}
                />
                <Legend
                  color="var(--muted-foreground)"
                  label={tr("interest")}
                  value={moneyDisplay(result.totalInterest, lang, bnDigits, 0)}
                  lang={lang}
                />
                <p
                  className={cn(
                    "pt-2 text-xs text-muted-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {RATES_DISCLAIMER[lang]}
                </p>
              </div>
            </div>

            {/* Schedule */}
            <div className="rounded-xl border border-border bg-card p-3 md:p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
                  {tr("schedule")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={downloadCSV} className="gap-1.5">
                    <FileSpreadsheet size={14} />
                    {tr("downloadCsv")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={doPrint} className="gap-1.5">
                    <Printer size={14} />
                    {tr("print")}
                  </Button>
                  <ShareButton
                    title={`${tr("payment")} (${freqLabel(frequency)}): ${moneyDisplay(result.payment, lang, bnDigits, 0)}`}
                  />
                </div>
              </div>
              <Tabs defaultValue="yearly">
                <TabsList>
                  <TabsTrigger value="yearly" className={lang === "bn" ? "font-bn" : ""}>
                    {tr("yearly")}
                  </TabsTrigger>
                  <TabsTrigger value="periodic" className={lang === "bn" ? "font-bn" : ""}>
                    {tr("perPeriod")}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="yearly" className="mt-3">
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                            {tr("year")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("principalPaid")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("interestPaid")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("balance")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.yearly.map((y) => (
                          <tr key={y.year} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums">{num(y.year)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(y.principalPaid, lang, bnDigits, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(y.interestPaid, lang, bnDigits, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(y.balance, lang, bnDigits, 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
                <TabsContent value="periodic" className="mt-3">
                  <div className="max-h-96 overflow-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted/80 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
                        <tr>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                            {tr("period")}
                          </th>
                          <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                            {tr("date")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("payment")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("principal")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("interest")}
                          </th>
                          <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                            {tr("balance")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.schedule.map((p) => (
                          <tr key={p.index} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums">{num(p.index)}</td>
                            <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                              {lang === "bn"
                                ? toBengaliNumerals(format(p.date, "dd MMM yyyy"))
                                : format(p.date, "dd MMM yyyy")}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(p.payment, lang, bnDigits, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(p.principal, lang, bnDigits, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(p.interest, lang, bnDigits, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {moneyDisplay(p.balance, lang, bnDigits, 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        )}

        {/* Educational content */}
        <article className="mt-12 space-y-10">
          <Section title={tr("whatTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("whatBody")}
            </p>
          </Section>
          <Section title={tr("reduceVsFlatTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("reduceVsFlatBody")}
            </p>
          </Section>
          <Section title={tr("tenureTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("tenureBody")}
            </p>
          </Section>
          <Section title={tr("ratesTitle")} lang={lang}>
            <RatesTable lang={lang} />
            <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {RATES_DISCLAIMER[lang]}
            </p>
          </Section>
          <Section title={tr("prepayTitle")} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("prepayBody")}
            </p>
          </Section>
        </article>
      </TooltipProvider>
    </CalculatorLayout>
  );
}

/* ---------- Helpers ---------- */

function moneyDisplay(value: number, lang: "en" | "bn", bnDigits: boolean, frac = 0): string {
  const useBn = lang === "bn" || bnDigits;
  return formatBDT(Math.round(value * 100) / 100, useBn ? "bn" : "en", frac);
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p
        className={cn(
          "text-xs font-semibold uppercase tracking-wide",
          accent ? "text-accent" : "text-muted-foreground",
        )}
      >
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Legend({
  color,
  label,
  value,
  lang,
}: {
  color: string;
  label: string;
  value: string;
  lang: "en" | "bn";
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: color }} />
        {label}
      </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function RatesTable({ lang }: { lang: "en" | "bn" }) {
  const head = {
    bank: { en: "Bank", bn: "ব্যাংক" },
    personal: { en: "Personal", bn: "পার্সোনাল" },
    home: { en: "Home", bn: "হোম" },
    car: { en: "Car", bn: "কার" },
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{head.bank[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.personal[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.home[lang]}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{head.car[lang]}</th>
          </tr>
        </thead>
        <tbody>
          {BD_BANKS.filter((b) => b.id !== "custom").map((b) => (
            <tr key={b.id} className="border-t border-border">
              <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{b.name[lang]}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {b.rates.personal != null
                  ? `${lang === "bn" ? toBengaliNumerals(b.rates.personal) : b.rates.personal}%`
                  : "—"}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {b.rates.home != null
                  ? `${lang === "bn" ? toBengaliNumerals(b.rates.home) : b.rates.home}%`
                  : "—"}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {b.rates.car != null
                  ? `${lang === "bn" ? toBengaliNumerals(b.rates.car) : b.rates.car}%`
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
